import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Trophy, Star, Clock, Zap, Target, TrendingUp, Lightbulb, ThumbsUp, Sparkles, Award, Brain, Send, MessageSquare } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const MONTH_NAMES = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const BADGE_CONFIG = {
  best_hours: { icon: Clock, gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  most_productive: { icon: Zap, gradient: 'from-blue-500 to-cyan-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  most_constant: { icon: Target, gradient: 'from-emerald-500 to-green-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  most_engaged: { icon: Star, gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-50', border: 'border-violet-200' },
};

const IDEA_STATUS = {
  nouvelle: { label: 'Nouvelle', color: 'bg-blue-100 text-blue-700' },
  en_cours: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  approuvee: { label: 'Approuvee', color: 'bg-emerald-100 text-emerald-700' },
  refusee: { label: 'Refusee', color: 'bg-red-100 text-red-700' },
};

const InsightsPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [showIdeaForm, setShowIdeaForm] = useState(false);
  const [ideaForm, setIdeaForm] = useState({ title: '', description: '', category: 'amelioration' });
  const [tab, setTab] = useState('performance');
  const [analyzingIdea, setAnalyzingIdea] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [insightsRes, ideasRes] = await Promise.all([
        axios.get(`${API}/insights/monthly/${year}/${month}`),
        axios.get(`${API}/insights/ideas`),
      ]);
      setData(insightsRes.data);
      setIdeas(ideasRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { loadData(); }, [loadData]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const generateAiSummary = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/insights/ai-summary/${year}/${month}`);
      setAiSummary(res.data.summary);
    } catch (err) { toast.error('Erreur generation IA'); }
    finally { setAiLoading(false); }
  };

  const submitIdea = async () => {
    if (!ideaForm.title || !ideaForm.description) { toast.error('Titre et description requis'); return; }
    try {
      await axios.post(`${API}/insights/ideas`, { ...ideaForm, employee_id: auth?.admin?.id || '' });
      toast.success('Idee soumise');
      setShowIdeaForm(false);
      setIdeaForm({ title: '', description: '', category: 'amelioration' });
      loadData();
    } catch (err) { toast.error('Erreur'); }
  };

  const analyzeIdea = async (ideaId) => {
    setAnalyzingIdea(ideaId);
    try {
      await axios.post(`${API}/insights/ideas/${ideaId}/analyze`);
      toast.success('Analyse IA generee');
      loadData();
    } catch (err) { toast.error('Erreur analyse'); }
    finally { setAnalyzingIdea(null); }
  };

  const voteIdea = async (ideaId) => {
    try {
      await axios.post(`${API}/insights/ideas/${ideaId}/vote`);
      loadData();
    } catch {}
  };

  const updateIdeaStatus = async (ideaId, status) => {
    try {
      await axios.put(`${API}/insights/ideas/${ideaId}`, { status });
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const scoreColor = (score) => {
    if (score >= 70) return 'text-emerald-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-red-500';
  };

  const scoreBar = (score) => {
    if (score >= 70) return 'bg-emerald-500';
    if (score >= 40) return 'bg-amber-500';
    return 'bg-red-400';
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto" data-testid="insights-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 flex items-center gap-2" data-testid="insights-title">
              <Sparkles className="w-7 h-7 text-amber-500" /> Vortekio Insights
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Performance, motivation et idees</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-semibold text-slate-900 min-w-[150px] text-center">{MONTH_NAMES[month - 1]} {year}</span>
            <Button variant="outline" size="sm" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b pb-2">
          {[{ key: 'performance', label: 'Performance', icon: Trophy }, { key: 'ideas', label: 'Boite a idees', icon: Lightbulb }].map(t => (
            <Button key={t.key} variant={tab === t.key ? 'default' : 'ghost'} size="sm" onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
              <t.icon className="w-4 h-4 mr-1.5" /> {t.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-slate-400 text-center py-20">{t('common.loading')}</div>
        ) : tab === 'performance' ? (
          <>
            {/* Badges / Podium */}
            {data?.badges && Object.keys(data.badges).length > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="badges-section">
                {Object.entries(data.badges).map(([key, badge]) => {
                  const config = BADGE_CONFIG[key] || BADGE_CONFIG.most_engaged;
                  const Icon = config.icon;
                  return (
                    <Card key={key} className={`p-5 ${config.bg} ${config.border} border-2 relative overflow-hidden`}>
                      <div className={`absolute top-0 right-0 w-20 h-20 rounded-bl-full bg-gradient-to-br ${config.gradient} opacity-10`} />
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{badge.label}</span>
                      </div>
                      <p className="text-lg font-bold text-slate-900">{badge.name}</p>
                      <p className={`text-2xl font-black bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}>{badge.value}</p>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Employee Performance Table */}
            <Card className="overflow-hidden" data-testid="performance-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-900 text-white">
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider w-8">#</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Employe</th>
                      <th className="text-center py-3 px-3 text-xs uppercase tracking-wider">Jours</th>
                      <th className="text-center py-3 px-3 text-xs uppercase tracking-wider">Heures</th>
                      <th className="text-center py-3 px-3 text-xs uppercase tracking-wider">Fiches</th>
                      <th className="text-center py-3 px-3 text-xs uppercase tracking-wider hidden md:table-cell">Ponctualite</th>
                      <th className="text-center py-3 px-3 text-xs uppercase tracking-wider hidden md:table-cell">Retards</th>
                      <th className="text-center py-3 px-3 text-xs uppercase tracking-wider hidden lg:table-cell">Hors fiche</th>
                      <th className="text-center py-3 px-3 text-xs uppercase tracking-wider hidden lg:table-cell">Notes</th>
                      <th className="text-center py-3 px-3 text-xs uppercase tracking-wider">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.employees || []).sort((a, b) => b.engagement_score - a.engagement_score).map((emp, idx) => {
                      const rank = idx + 1;
                      const isTop3 = rank <= 3;
                      const medalColors = ['text-amber-500', 'text-slate-400', 'text-amber-700'];
                      return (
                        <tr key={emp.employee_id} className={`border-b border-slate-100 ${isTop3 ? 'bg-amber-50/30' : 'hover:bg-slate-50'}`}>
                          <td className="py-3 px-4">
                            {isTop3 ? (
                              <Trophy className={`w-5 h-5 ${medalColors[idx]}`} />
                            ) : (
                              <span className="text-slate-400 font-mono">{rank}</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <p className={`font-semibold ${isTop3 ? 'text-slate-900' : 'text-slate-700'}`}>{emp.name}</p>
                            <p className="text-[11px] text-slate-400">{emp.position || emp.role}</p>
                          </td>
                          <td className="py-3 px-3 text-center font-medium">{emp.days_present}</td>
                          <td className="py-3 px-3 text-center font-bold text-slate-900">{emp.total_hours}h</td>
                          <td className="py-3 px-3 text-center">
                            <span className="font-bold text-blue-600">{emp.work_orders}</span>
                          </td>
                          <td className="py-3 px-3 text-center hidden md:table-cell">
                            <span className={`font-medium ${emp.on_time_pct >= 90 ? 'text-emerald-600' : emp.on_time_pct >= 70 ? 'text-amber-600' : 'text-red-500'}`}>{emp.on_time_pct}%</span>
                          </td>
                          <td className="py-3 px-3 text-center hidden md:table-cell">
                            {emp.late_count > 0 ? <span className="text-red-500 font-medium">{emp.late_count}</span> : <span className="text-emerald-500">0</span>}
                          </td>
                          <td className="py-3 px-3 text-center hidden lg:table-cell text-slate-600">{emp.offticket_count}</td>
                          <td className="py-3 px-3 text-center hidden lg:table-cell text-slate-600">{emp.notes_count}</td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2 justify-center">
                              <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${scoreBar(emp.engagement_score)}`} style={{ width: `${emp.engagement_score}%` }} />
                              </div>
                              <span className={`text-sm font-bold ${scoreColor(emp.engagement_score)}`}>{emp.engagement_score}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* AI Summary */}
            <Card className="p-6 bg-gradient-to-br from-slate-50 to-indigo-50/30 border-indigo-100" data-testid="ai-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-base font-semibold text-slate-900">Analyse IA du mois</h2>
                </div>
                <Button size="sm" onClick={generateAiSummary} disabled={aiLoading} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="generate-ai-btn">
                  {aiLoading ? 'Analyse en cours...' : <><Sparkles className="w-3 h-3 mr-1" /> Generer</>}
                </Button>
              </div>
              {aiSummary ? (
                <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap" data-testid="ai-summary-content">{aiSummary}</div>
              ) : (
                <p className="text-sm text-slate-400 italic">Cliquez sur "Generer" pour obtenir une analyse IA personnalisee du mois.</p>
              )}
            </Card>
          </>
        ) : (
          /* IDEAS TAB */
          <>
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-500">{ideas.length} idee{ideas.length > 1 ? 's' : ''} soumise{ideas.length > 1 ? 's' : ''}</p>
              <Button onClick={() => setShowIdeaForm(true)} data-testid="new-idea-btn">
                <Lightbulb className="w-4 h-4 mr-2" /> Nouvelle idee
              </Button>
            </div>

            {ideas.length === 0 ? (
              <Card className="p-12 text-center">
                <Lightbulb className="w-12 h-12 mx-auto mb-3 text-amber-300" />
                <p className="text-slate-500">Aucune idee pour le moment</p>
                <p className="text-xs text-slate-400 mt-1">Les employes peuvent soumettre leurs idees depuis leur dashboard</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {ideas.map(idea => {
                  const st = IDEA_STATUS[idea.status] || IDEA_STATUS.nouvelle;
                  return (
                    <Card key={idea.id} className="p-5" data-testid={`idea-${idea.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-semibold text-slate-900">{idea.title}</h3>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.color}`}>{st.label}</span>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">{idea.description}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-400">
                            <span>{idea.employee_name}</span>
                            <span>{new Date(idea.created_at).toLocaleDateString('fr-CH')}</span>
                            <span className="capitalize">{idea.category}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => voteIdea(idea.id)} className="text-slate-400 hover:text-amber-500">
                            <ThumbsUp className="w-4 h-4 mr-1" /> {idea.votes || 0}
                          </Button>
                        </div>
                      </div>

                      {/* AI Analysis */}
                      {idea.ai_analysis ? (
                        <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                          <p className="text-xs font-semibold text-indigo-600 mb-1 flex items-center gap-1"><Brain className="w-3 h-3" /> Analyse IA</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{idea.ai_analysis}</p>
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => analyzeIdea(idea.id)} disabled={analyzingIdea === idea.id} className="mt-3 text-xs" data-testid={`analyze-idea-${idea.id}`}>
                          {analyzingIdea === idea.id ? 'Analyse...' : <><Brain className="w-3 h-3 mr-1" /> Analyser avec l'IA</>}
                        </Button>
                      )}

                      {/* Admin actions */}
                      {idea.status === 'nouvelle' && (
                        <div className="mt-3 flex gap-2">
                          <Button size="sm" onClick={() => updateIdeaStatus(idea.id, 'approuvee')} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white">Approuver</Button>
                          <Button size="sm" variant="outline" onClick={() => updateIdeaStatus(idea.id, 'en_cours')} className="text-xs">{t('dashboard.in_progress')}</Button>
                          <Button size="sm" variant="outline" onClick={() => updateIdeaStatus(idea.id, 'refusee')} className="text-xs text-red-500 border-red-200 hover:bg-red-50">Refuser</Button>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* New Idea Dialog */}
      <Dialog open={showIdeaForm} onOpenChange={setShowIdeaForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lightbulb className="w-5 h-5 text-amber-500" /> Nouvelle idee</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs text-slate-500">Titre *</label>
              <Input value={ideaForm.title} onChange={e => setIdeaForm({ ...ideaForm, title: e.target.value })} className="mt-1" placeholder="Ex: Installer un compresseur plus puissant" data-testid="idea-title-input" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Description *</label>
              <textarea value={ideaForm.description} onChange={e => setIdeaForm({ ...ideaForm, description: e.target.value })} className="w-full border rounded-lg p-2 text-sm h-24 mt-1" placeholder="Decrivez votre idee en detail..." data-testid="idea-desc-input" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Categorie</label>
              <select value={ideaForm.category} onChange={e => setIdeaForm({ ...ideaForm, category: e.target.value })} className="w-full border rounded-lg p-2 text-sm mt-1">
                <option value="amelioration">Amelioration</option>
                <option value="investissement">Investissement</option>
                <option value="organisation">Organisation</option>
                <option value="securite">Securite</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowIdeaForm(false)}>{t('common.cancel')}</Button>
              <Button onClick={submitIdea} data-testid="submit-idea-btn"><Send className="w-4 h-4 mr-1" /> Soumettre</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default InsightsPage;
